import { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { api } from '../api';

// Admin panel (shown from a game card) to generate no-login pairing codes
// for a remote Scorer or Stats device, and revoke active ones.

interface RemoteCode {
  id: string;
  role: 'SCORER' | 'STATS';
  code: string;
  status: 'pending' | 'active' | 'expired';
  claimedAt: string | null;
  codeExpiresAt: string;
  deviceLabel: string | null;
  createdAt: string;
}

function joinUrl(code: string): string {
  // The QR points at the public /join page on this same app origin.
  const origin =
    typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}/join?code=${code}`;
}

export function RemoteCodePanel({
  gameId,
  onClose,
}: {
  gameId: string;
  onClose: () => void;
}) {
  const [codes, setCodes] = useState<RemoteCode[]>([]);
  const [fresh, setFresh] = useState<RemoteCode | null>(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    setCodes(await api.listRemoteCodes(gameId));
  }
  // Poll while open so "waiting" flips to "connected" when a device pairs.
  useEffect(() => {
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId]);

  async function generate(role: 'SCORER' | 'STATS') {
    setBusy(true);
    try {
      const res = await api.createRemoteCode(gameId, role);
      setFresh({
        id: res.id,
        role: res.role,
        code: res.code,
        status: 'pending',
        claimedAt: null,
        codeExpiresAt: res.codeExpiresAt,
        deviceLabel: null,
        createdAt: new Date().toISOString(),
      });
      await load();
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: string) {
    setBusy(true);
    try {
      await api.revokeRemoteSession(id);
      if (fresh?.id === id) setFresh(null);
      await load();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-xl border border-border bg-card p-5 shadow-2xl">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">Remote devices</h2>
          <button onClick={onClose} className="btn btn-ghost text-sm">
            Close
          </button>
        </div>
        <p className="mt-1 text-sm text-muted">
          Pair an iPad with no login. The code is single-use and expires in
          10 minutes; the device stays connected until the game ends or you
          revoke it.
        </p>

        <div className="mt-4 flex gap-2">
          <button
            className="btn btn-primary flex-1"
            onClick={() => generate('SCORER')}
            disabled={busy}
          >
            + Score code
          </button>
          <button
            className="btn btn-secondary flex-1"
            onClick={() => generate('STATS')}
            disabled={busy}
          >
            + Stats code
          </button>
        </div>

        {fresh && (
          <div className="mt-4 rounded-lg border-2 border-brand/40 bg-brand/5 p-4 text-center">
            <div className="text-xs uppercase tracking-wider text-muted">
              {fresh.role === 'SCORER' ? 'Scorer' : 'Stats'} pairing code
            </div>
            <div className="mx-auto mt-3 w-fit rounded-lg bg-white p-3">
              <QRCodeSVG value={joinUrl(fresh.code)} size={160} />
            </div>
            <div className="mt-3 font-mono text-4xl font-bold tracking-[0.3em]">
              {fresh.code}
            </div>
            <div className="mt-1 text-xs text-muted">
              Scan the QR, or go to{' '}
              <span className="font-mono">/join</span> and enter the code.
            </div>
          </div>
        )}

        <div className="mt-5">
          <div className="mb-2 text-xs uppercase tracking-wider text-muted">
            Active & pending
          </div>
          {codes.length === 0 ? (
            <p className="text-sm text-muted">No remote devices yet.</p>
          ) : (
            <ul className="space-y-1.5">
              {codes.map((c) => (
                <li
                  key={c.id}
                  className="flex items-center justify-between rounded-md border border-border bg-surface px-3 py-2 text-sm"
                >
                  <span>
                    <span className="font-mono font-bold">{c.code}</span>{' '}
                    <span className="text-muted">
                      · {c.role === 'SCORER' ? 'Scorer' : 'Stats'} ·{' '}
                    </span>
                    <span
                      className={
                        c.status === 'active'
                          ? 'text-emerald-300'
                          : c.status === 'expired'
                          ? 'text-muted'
                          : 'text-amber-300'
                      }
                    >
                      {c.status === 'active'
                        ? 'connected'
                        : c.status === 'expired'
                        ? 'expired'
                        : 'waiting'}
                    </span>
                  </span>
                  <button
                    onClick={() => revoke(c.id)}
                    disabled={busy}
                    className="text-xs text-red-300 hover:underline disabled:opacity-50"
                  >
                    Revoke
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
